'use client'
import {
  __objRest,
  __spreadProps,
  __spreadValues
} from "../chunk-5N2UUO5C.js";

// src/components/panel/panel.tsx
import { useCallback, useEffect, useState } from "react";

// src/utils/index.ts
import clsx from "clsx";
import { twMerge } from "tailwind-merge";
function cn(...inputs) {
  return twMerge(clsx(inputs));
}
function getChildren(value, displayName) {
  var _a, _b;
  const childrenArray = Array.isArray(value) ? value : [value];
  for (const child of childrenArray) {
    if (((_a = child == null ? void 0 : child.type) == null ? void 0 : _a.displayName) === displayName) {
      return child;
    }
    if ((_b = child == null ? void 0 : child.props) == null ? void 0 : _b.children) {
      const nestedChild = getChildren(child.props.children, displayName);
      if (nestedChild) {
        return nestedChild;
      }
    }
  }
  return void 0;
}

// src/components/panel/panel-header.tsx
import { jsx } from "react/jsx-runtime";
var PanelHeader = (_a) => {
  var _b = _a, { children, className } = _b, props = __objRest(_b, ["children", "className"]);
  return /* @__PURE__ */ jsx(
    "h1",
    __spreadProps(__spreadValues({
      className: cn(
        "panel-header text-xl text-left w-full text-white font-semibold  mb-6",
        className
      )
    }, props), {
      children
    })
  );
};
PanelHeader.displayName = "MagicPanel.Header";

// src/components/panel/panel-close.tsx
import React from "react";
import { jsx as jsx2 } from "react/jsx-runtime";
var PanelClose = React.forwardRef(
  (_a, ref) => {
    var _b = _a, { className, onClose } = _b, props = __objRest(_b, ["className", "onClose"]);
    const cls = className && /(left|bottom)-(?:\d+|\d+\/\d+|auto|full|px)$/.test(className) ? className : cn(className);
    return /* @__PURE__ */ jsx2(
      "div",
      __spreadValues(__spreadProps(__spreadValues({}, props), {
        id: "magic-close",
        ref,
        className: cn("magic-close close:bg-white", cls),
        onClick: () => onClose && onClose()
      }), props)
    );
  }
);
PanelClose.displayName = "MagicPanel.Close";

// src/components/panel/panel-overlay.tsx
import { jsx as jsx3 } from "react/jsx-runtime";
var PanelOverlay = (_a) => {
  var _b = _a, { onClose, className } = _b, props = __objRest(_b, ["onClose", "className"]);
  return /* @__PURE__ */ jsx3(
    "div",
    __spreadValues({
      className: cn("magic-overlay opacity-100  bg-black/80", className),
      onClick: () => onClose && onClose()
    }, props)
  );
};
PanelOverlay.displayName = "MagicPanel.Overlay";

// src/components/content/content.tsx
import { jsx as jsx4 } from "react/jsx-runtime";
var Content = (_a) => {
  var _b = _a, {
    children,
    className
  } = _b, props = __objRest(_b, [
    "children",
    "className"
  ]);
  return /* @__PURE__ */ jsx4(
    "div",
    __spreadProps(__spreadValues({
      className: cn(
        "flex flex-col gap-3 h-full",
        className
      )
    }, props), {
      children
    })
  );
};

// src/components/content-scroll/content-scroll.tsx
import { jsx as jsx5 } from "react/jsx-runtime";
var ContentScroll = (_a) => {
  var _b = _a, {
    children,
    className
  } = _b, props = __objRest(_b, [
    "children",
    "className"
  ]);
  return /* @__PURE__ */ jsx5(
    "div",
    __spreadProps(__spreadValues({
      className: cn(
        " flex flex-col gap-3 overflow-auto h-full max-h-[calc(100%-64px)]",
        className
      )
    }, props), {
      children
    })
  );
};

// src/components/panel/panel.tsx
import { jsx as jsx6, jsxs } from "react/jsx-runtime";
var MagicPanel = ({
  children,
  open = false,
  onChange,
  destroyOnClose,
  width,
  height,
  className,
  drawer = false,
  placement
}) => {
  const placementPosition = {
    top: {
      animationIn: "animate__slideInDown",
      animationOut: "animate__slideOutUp",
      width: "100%",
      height: "400px",
      className: "max-h-[calc(100%-55px)] rounded-b-lg"
    },
    bottom: {
      animationIn: "animate__slideInUp",
      animationOut: "animate__slideOutDown",
      width: "100%",
      height: "400px",
      className: "max-h-[calc(100%-55px)] rounded-t-lg"
    },
    left: {
      animationIn: "animate__slideInLeft",
      animationOut: "animate__slideOutLeft",
      width: "400px",
      height: "100%",
      className: "rounded-r-lg"
    },
    right: {
      animationIn: "animate__slideInRight",
      animationOut: "animate__slideOutRight",
      width: "400px",
      height: "100%",
      className: "rounded-l-lg"
    }
  };
  const animationPanelIn = drawer ? placementPosition[placement || "right"].animationIn : "animate__zoomIn";
  const animationPanelOut = drawer ? placementPosition[placement || "right"].animationOut : "animate__zoomOut";
  const isDrawer = drawer ? "drawer" : "modal";
  const [animation, setAnimation] = useState(animationPanelIn);
  const [opacity, setOpacity] = useState(0);
  const [display, setDisplay] = useState("none");
  const toggle = useCallback(
    (value) => {
      if (open) {
        setDisplay("flex");
        setOpacity(1);
        setAnimation(animationPanelIn);
        onChange && onChange(value);
        const body = document.body;
        const hasVerticalScrollbar = body.scrollHeight > body.clientHeight;
        document.body.style.overflow = "hidden";
        if (hasVerticalScrollbar) {
          if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent
          )) {
            document.body.classList.add("magic-panel-scroll-mobile");
            return;
          } else {
            document.body.classList.add("magic-panel-scroll");
          }
        }
        const time2 = setTimeout(() => {
          clearTimeout(time2);
        }, 300);
        return;
      }
      setOpacity(0);
      setAnimation(animationPanelOut);
      const time = setTimeout(() => {
        setDisplay("none");
        onChange && onChange(false);
        document.body.style.overflow = "";
        if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        )) {
          document.body.classList.remove("magic-panel-scroll-mobile");
          return;
        }
        document.body.classList.remove("magic-panel-scroll");
        clearTimeout(time);
      }, 150);
    },
    [animationPanelIn, animationPanelOut, onChange, open]
  );
  useEffect(() => {
    toggle(open);
  }, [animationPanelIn, open, toggle]);
  const overlay = getChildren(children, "MagicPanel.Overlay");
  const panel = /* @__PURE__ */ jsxs(
    "div",
    {
      id: "panel",
      "data-open": open,
      className: cn(
        "magic-panel-wrapper transition-all",
        isDrawer,
        isDrawer ? placement ? `placement-${placement}` : "" : "placement-right"
      ),
      style: { display, opacity },
      children: [
        overlay || /* @__PURE__ */ jsx6(PanelOverlay, { onClose: () => toggle(false) }),
        /* @__PURE__ */ jsx6(
          "div",
          {
            className: cn(
              `magic-panel-content bg-background p-6   content--${isDrawer} animate__animated animate__faster`,
              animation,
              `placement-content-${placement}`,
              "border border-border",
              !drawer ? "max-h-[calc(100%-92px)] rounded-lg" : placementPosition[placement || "right"].className,
              className
            ),
            style: {
              maxWidth: width || placementPosition[placement || "right"].width,
              width: "100%",
              height: drawer ? height || placementPosition[placement || "right"].height : 500
            },
            children: Array.isArray(children) ? children.filter(
              (child) => {
                var _a;
                return ((_a = child == null ? void 0 : child.type) == null ? void 0 : _a.displayName) !== "MagicPanel.Overlay";
              }
            ) : children
          }
        )
      ]
    }
  );
  if (destroyOnClose) {
    return open && /* @__PURE__ */ jsx6("div", { className: "magic-panel", children: panel });
  }
  return /* @__PURE__ */ jsx6("div", { className: "magic-panel", children: panel });
};
MagicPanel.Header = PanelHeader;
MagicPanel.Close = PanelClose;
MagicPanel.Overlay = PanelOverlay;
MagicPanel.Content = Content;
MagicPanel.ContentScroll = ContentScroll;
export {
  MagicPanel,
  PanelClose,
  PanelHeader
};
